import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import WorkPageHeader from '../../components/work/WorkPageHeader';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Empty,
  Input,
  message,
  Popconfirm,
  Result,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CommentOutlined,
  ReloadOutlined,
  SendOutlined,
  CheckCircleOutlined,
  UndoOutlined,
  UserAddOutlined,
  PaperClipOutlined,
  PictureOutlined,
  SoundOutlined,
  MutedOutlined,
  AudioOutlined,
  DeleteOutlined,
  ArrowRightOutlined,
  SwapOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { liveChatApi, ForbiddenError } from '../../utils/api';
import { useSocket, useSocketEvent } from '../../hooks/useSocket';
import ChatMessageList from '../../components/live-chat/ChatMessageList';
import useIncomingSound from '../../components/live-chat/useIncomingSound';
import useVoiceRecorder, { canRecordVoice } from '../../components/live-chat/useVoiceRecorder';
import {
  SourceTag,
  SiteTag,
  OPERATOR_STATUS,
  LIVECHAT_SOCKET_EVENTS,
  sortMessages,
  contactName,
  operatorName,
  formatChatTime,
} from '../../components/live-chat/liveChatMeta';
import ConversationFilters, { DEFAULT_FILTERS } from '../../components/live-chat/ConversationFilters';
import TransferConversationModal from '../../components/live-chat/TransferConversationModal';
import ConversationHistoryDrawer from '../../components/live-chat/ConversationHistoryDrawer';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const POLL_CONNECTED_MS = 30000;
const POLL_DISCONNECTED_MS = 8000;

const LiveChatInbox = () => {
  const { isConnected } = useSocket();
  const [me, setMe] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [loadingMe, setLoadingMe] = useState(true);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sites, setSites] = useState([]);
  const [departmentCounts, setDepartmentCounts] = useState({});
  const [conversations, setConversations] = useState([]);
  const [counts, setCounts] = useState({});
  const [loadingList, setLoadingList] = useState(false);

  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [canReply, setCanReply] = useState(false);
  const [canTransfer, setCanTransfer] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [draft, setDraft] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  // گفتگوهای اینستاگرامی که دریافت پیام‌های قبلی‌شان در جریان است / در این صفحه انجام شده
  const [historyLoadingIds, setHistoryLoadingIds] = useState(() => new Set());
  const instagramHistorySynced = useRef(new Set());

  const { play: playSound, muted, toggleMuted } = useIncomingSound();
  const unreadSnapshot = useRef(null);
  const notifyRef = useRef(() => {});
  const activeIdRef = useRef(null);

  // ارتفاع ثابت صندوق تا انتهای پنجره؛ هر ستون جداگانه اسکرول می‌خورد
  const layoutRef = useRef(null);
  const [paneHeight, setPaneHeight] = useState(560);
  // دو ستونه فقط وقتی خود صندوق جا دارد (نه بر اساس عرض صفحه؛ سایدبار ERP هم فضا می‌گیرد)
  const TWO_COLUMN_MIN_WIDTH = 760;
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1280);
  const messagesRef = useRef([]);
  activeIdRef.current = activeId;
  messagesRef.current = messages;

  const loadMe = useCallback(async () => {
    try {
      const res = await liveChatApi.getMe();
      setMe(res?.data ?? null);
    } catch (error) {
      if (error instanceof ForbiddenError || error?.status === 403) setForbidden(true);
      else message.error(error?.message || 'خطا در دریافت اطلاعات اپراتور');
    } finally {
      setLoadingMe(false);
    }
  }, []);

  const loadList = useCallback(async (silent = false) => {
    if (!silent) setLoadingList(true);
    try {
      const res = await liveChatApi.getConversations({
        status: filters.status,
        assigned: filters.assigned,
        ...(filters.source_id ? { source_id: filters.source_id } : {}),
        ...(filters.site ? { site: filters.site } : {}),
        ...(filters.department_id ? { department_id: filters.department_id } : {}),
        ...(filters.sub_department_id ? { sub_department_id: filters.sub_department_id } : {}),
        ...(filters.search ? { search: filters.search } : {}),
        per_page: 50,
      });
      const rows = res?.data ?? [];
      // پیام جدید مشتری بین دو همگام‌سازی (وقتی سوکت قطع است یا رویدادی از دست رفته)
      // فقط در همگام‌سازی پس‌زمینه؛ تعویض فیلتر نباید صدا پخش کند
      const previous = unreadSnapshot.current;
      if (silent && previous) {
        const grown = rows.find((c) => (c.unread_count || 0) > (previous.get(c.id) ?? 0));
        if (grown) notifyRef.current(grown.id);
      }
      unreadSnapshot.current = new Map(rows.map((c) => [c.id, c.unread_count || 0]));
      setConversations(rows);
      setCounts(res?.counts ?? {});
      setSites(res?.sites ?? []);
      setDepartmentCounts(res?.department_counts ?? {});
    } catch (error) {
      if (!silent) message.error(error?.message || 'خطا در دریافت گفتگوها');
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, [filters]);

  /** پیام‌های قبلی مخاطب در دایرکت اینستاگرام؛ خودکار با باز شدن گفتگو، یک بار برای هر گفتگو تا صفحه باز است */
  const syncInstagramHistory = useCallback(async (id) => {
    if (instagramHistorySynced.current.has(id)) return;
    instagramHistorySynced.current.add(id);
    setHistoryLoadingIds((prev) => new Set(prev).add(id));
    try {
      const res = await liveChatApi.importInstagramHistory(id);
      if (res?.data?.imported > 0 && activeIdRef.current === id) {
        message.success(res.message || 'پیام‌های قبلی بارگذاری شد');
        await reloadActiveMessagesRef.current?.();
      }
    } catch {
      // بی‌صدا؛ با باز کردن دوباره گفتگو تلاش می‌شود
      instagramHistorySynced.current.delete(id);
    } finally {
      setHistoryLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  const openConversation = useCallback(async (id) => {
    setActiveId(id);
    setLoadingThread(true);
    setDraft('');
    try {
      const res = await liveChatApi.getConversation(id);
      const data = res?.data ?? {};
      setActive(data.conversation ?? null);
      setMessages(data.messages?.items ?? []);
      setHasMore(Boolean(data.messages?.has_more));
      setCanReply(Boolean(data.can_reply));
      setCanTransfer(Boolean(data.can_transfer));
      if (data.can_reply && data.conversation?.source?.key === 'instagram' && data.conversation?.channel_id) {
        syncInstagramHistory(id);
      }
      if (data.conversation?.unread_count > 0) {
        liveChatApi.markRead(id).catch(() => {});
        setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c)));
      }
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت گفتگو');
      setActiveId(null);
      setActive(null);
    } finally {
      setLoadingThread(false);
    }
  }, [syncInstagramHistory]);

  // باز کردن گفتگو از لینک (مثلاً از داشبورد): /live-chat?conversation=ID
  useEffect(() => {
    const id = Number(new URLSearchParams(window.location.search).get('conversation'));
    if (id > 0 && !loadingMe && !forbidden) openConversation(id);
    // فقط یک بار پس از آماده‌شدن اطلاعات کاربر
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMe]);

  /** فقط پیام‌های جدیدتر از آخرین پیام نمایش‌داده‌شده */
  const syncActiveMessages = useCallback(async () => {
    const id = activeIdRef.current;
    if (!id) return;
    const current = messagesRef.current;
    const lastId = current.reduce((max, m) => Math.max(max, m.id), 0);
    try {
      const res = await liveChatApi.getMessages(id, { after_id: lastId });
      const items = res?.data?.items ?? [];
      if (items.length && activeIdRef.current === id) {
        setMessages((prev) => {
          const known = new Set(prev.map((m) => m.id));
          return [...prev, ...items.filter((m) => !known.has(m.id))];
        });
        if (items.some((m) => m.direction === 'in')) {
          notifyRef.current(id);
          liveChatApi.markRead(id).catch(() => {});
        }
      }
    } catch {
      // همگام‌سازی پس‌زمینه؛ خطا در تلاش بعدی جبران می‌شود
    }
  }, []);

  const refreshActiveMeta = useCallback(async () => {
    const id = activeIdRef.current;
    if (!id) return;
    try {
      const res = await liveChatApi.getConversation(id);
      if (activeIdRef.current !== id) return;
      setActive(res?.data?.conversation ?? null);
      setCanReply(Boolean(res?.data?.can_reply));
      setCanTransfer(Boolean(res?.data?.can_transfer));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    const el = layoutRef.current?.parentElement;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let lastWidth = -1;
    const apply = (rawWidth) => {
      const width = Math.round(rawWidth);
      if (width === lastWidth) return; // تغییر ارتفاع نباید چیدمان را عوض کند
      lastWidth = width;
      // حاشیه ±۳۰ پیکسل تا ظاهر/پنهان شدن اسکرول‌بار باعث رفت‌وبرگشت بی‌پایان نشود
      setIsDesktop((prev) => (prev ? width >= TWO_COLUMN_MIN_WIDTH - 30 : width >= TWO_COLUMN_MIN_WIDTH + 30));
    };
    const cs = getComputedStyle(el);
    // اندازه اولیه بدون انتظار برای فریم بعد (هم‌واحد با contentRect: بدون padding)
    apply(el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight));
    const observer = new ResizeObserver(([entry]) => apply(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, [me]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    let frame = 0;
    const apply = () => {
      const el = layoutRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const bottomGap = 44; // padding پایین کارت و محتوای صفحه
      setPaneHeight(Math.max(420, Math.round(window.innerHeight - top - bottomGap)));
    };
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
    };
  }, [isDesktop, me]);

  useEffect(() => {
    if (!me) return undefined;
    const t = setTimeout(() => loadList(), filters.search ? 350 : 0);
    return () => clearTimeout(t);
  }, [me, loadList, filters.search]);

  // پشتیبان بلادرنگ: همگام‌سازی دوره‌ای (سریع‌تر وقتی سوکت قطع است)
  useEffect(() => {
    if (!me) return undefined;
    const interval = setInterval(() => {
      loadList(true);
      syncActiveMessages();
    }, isConnected ? POLL_CONNECTED_MS : POLL_DISCONNECTED_MS);
    return () => clearInterval(interval);
  }, [me, isConnected, loadList, syncActiveMessages]);

  const listRefreshTimer = useRef(null);
  const scheduleListRefresh = useCallback(() => {
    clearTimeout(listRefreshTimer.current);
    listRefreshTimer.current = setTimeout(() => loadList(true), 400);
  }, [loadList]);
  useEffect(() => () => clearTimeout(listRefreshTimer.current), []);

  // اعلان پیام جدید مشتری: صدا و شمارنده در عنوان تب وقتی صفحه دیده نمی‌شود
  const hiddenUnread = useRef(0);
  const baseTitle = useRef(document.title);
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) {
        hiddenUnread.current = 0;
        document.title = baseTitle.current;
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    const title = baseTitle.current;
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      document.title = title;
    };
  }, []);

  const notifyInbound = useCallback(() => {
    playSound();
    if (document.hidden) {
      hiddenUnread.current += 1;
      document.title = `(${hiddenUnread.current}) پیام جدید — ${baseTitle.current}`;
    }
  }, [playSound]);
  notifyRef.current = notifyInbound;

  useSocketEvent(
    LIVECHAT_SOCKET_EVENTS.message,
    (data) => {
      scheduleListRefresh();
      if (data?.direction === 'in') notifyInbound(data.conversation_id);
      if (data?.conversation_id && Number(data.conversation_id) === Number(activeIdRef.current)) {
        syncActiveMessages();
      }
    },
    Boolean(me),
  );

  const myOperatorIdRef = useRef(null);
  const reloadActiveMessagesRef = useRef(null);
  const openConversationRef = useRef(openConversation);
  openConversationRef.current = openConversation;

  useSocketEvent(
    LIVECHAT_SOCKET_EVENTS.conversation,
    (data) => {
      scheduleListRefresh();
      // گفتگویی که همکار به من ارجاع داده (نه ارجاعی که خودم انجام دادم)
      const mine = myOperatorIdRef.current;
      if (
        data?.action === 'transferred'
        && mine
        && Number(data.assigned_operator_id) === Number(mine)
        && Number(data.from_operator_id) !== Number(mine)
      ) {
        playSound();
        message.info({
          content: <span className="custom-font cursor-pointer">یک گفتگو به شما ارجاع شد — برای باز کردن کلیک کنید</span>,
          duration: 8,
          onClick: () => openConversationRef.current(data.conversation_id),
        });
      }
      if (data?.conversation_id && Number(data.conversation_id) === Number(activeIdRef.current)) {
        refreshActiveMeta();
        // تاریخچه اینستاگرام از جای دیگر (همکار) وارد شد
        if (data?.action === 'history_imported') reloadActiveMessagesRef.current?.();
      }
    },
    Boolean(me),
  );

  const attachmentRules = me?.attachments;

  /** افزودن فایل به پیوست‌های پاسخ با بررسی پسوند/حجم/تعداد (بررسی اصلی در سرور) */
  const addFiles = useCallback((fileList) => {
    const rules = attachmentRules || {};
    const allowed = (rules.accept || '').split(',').map((x) => x.replace('.', '').trim().toLowerCase()).filter(Boolean);
    const maxBytes = (rules.max_mb || 10) * 1024 * 1024;
    const incoming = Array.from(fileList || []);
    setPendingFiles((prev) => {
      const next = [...prev];
      incoming.forEach((file) => {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (allowed.length && !allowed.includes(ext)) {
          message.warning(`نوع فایل «${file.name}» مجاز نیست`);
        } else if (file.size > maxBytes) {
          message.warning(`حجم «${file.name}» بیش از ${rules.max_mb} مگابایت است`);
        } else if (next.length >= (rules.max_files || 5)) {
          message.warning(`حداکثر ${rules.max_files || 5} فایل در هر پیام`);
        } else {
          next.push(file);
        }
      });
      return next;
    });
  }, [attachmentRules]);

  useEffect(() => {
    setPendingFiles([]);
  }, [activeId]);

  const sendReply = async () => {
    const body = draft.trim();
    if ((!body && pendingFiles.length === 0) || !activeId) return;
    setSending(true);
    try {
      const res = await liveChatApi.reply(activeId, body, pendingFiles);
      const msg = res?.data;
      if (msg) {
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      }
      setDraft('');
      setPendingFiles([]);
      refreshActiveMeta();
      scheduleListRefresh();
      // سرپرست بدون پروفایل اپراتور با اولین پاسخ اپراتور می‌شود؛ وضعیت و تب «من» به‌روز شود
      if (!me?.operator) loadMe();
    } catch (error) {
      message.error(error?.message || 'خطا در ارسال پیام');
    } finally {
      setSending(false);
    }
  };

  const sendVoice = useCallback(async (file, duration) => {
    const id = activeIdRef.current;
    if (!id) return;
    setSending(true);
    try {
      const res = await liveChatApi.reply(id, '', [file], [duration]);
      const msg = res?.data;
      if (msg && activeIdRef.current === id) {
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      }
      refreshActiveMeta();
      scheduleListRefresh();
    } catch (error) {
      message.error(error?.message || 'خطا در ارسال پیام صوتی');
    } finally {
      setSending(false);
    }
  }, [refreshActiveMeta, scheduleListRefresh]);

  const voice = useVoiceRecorder({
    onRecorded: sendVoice,
    onError: (text) => message.warning(text),
  });

  useEffect(() => {
    voice.stop(false);
  }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOlder = async () => {
    if (!activeId || !messages.length) return;
    setLoadingMore(true);
    try {
      // قدیمی‌ترین پیام بر اساس زمان ارسال (صفحه‌بندی سرور هم بر اساس زمان است)
      const res = await liveChatApi.getMessages(activeId, { before_id: sortMessages(messages)[0].id });
      setMessages((prev) => [...(res?.data?.items ?? []), ...prev]);
      setHasMore(Boolean(res?.data?.has_more));
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت پیام‌های قبلی');
    } finally {
      setLoadingMore(false);
    }
  };

  /** بارگذاری دوباره صفحه آخر پیام‌ها (پس از ورود تاریخچه) */
  const reloadActiveMessages = useCallback(async () => {
    const id = activeIdRef.current;
    if (!id) return;
    try {
      const res = await liveChatApi.getMessages(id);
      if (activeIdRef.current !== id) return;
      setMessages(res?.data?.items ?? []);
      setHasMore(Boolean(res?.data?.has_more));
    } catch {
      // در بارگذاری بعدی گفتگو جبران می‌شود
    }
  }, []);
  reloadActiveMessagesRef.current = reloadActiveMessages;

  const runAction = async (key, fn, successText) => {
    setActionLoading(key);
    try {
      await fn();
      if (successText) message.success(successText);
      await refreshActiveMeta();
      scheduleListRefresh();
    } catch (error) {
      message.error(error?.message || 'عملیات ناموفق بود');
    } finally {
      setActionLoading(null);
    }
  };

  const changeMyStatus = async (status) => {
    try {
      const res = await liveChatApi.updateMyStatus(status);
      setMe((prev) => ({ ...prev, operator: { ...prev.operator, ...(res?.data ?? { status }) } }));
    } catch (error) {
      message.error(error?.message || 'خطا در تغییر وضعیت');
    }
  };

  /** زیربخش‌های فعال بخش گفتگوی باز (از operators/me) */
  const subDepartmentOptions = useMemo(() => {
    if (!active?.department_id) return [];
    const dep = (me?.departments || []).find((d) => d.id === active.department_id);
    return (dep?.sub_departments || []).map((s) => ({ value: s.id, label: s.name }));
  }, [me, active]);

  /** اینستاگرام فقط تا ۲۴ ساعت پس از آخرین پیام مشتری اجازه پاسخ می‌دهد */
  const instagramWindowClosed = useMemo(() => {
    if (active?.source?.key !== 'instagram') return false;
    const lastInbound = [...messages].reverse().find((m) => m.direction === 'in')?.created_at || active.last_inbound_at;
    return Boolean(lastInbound) && Date.now() - new Date(lastInbound).getTime() > 24 * 60 * 60 * 1000;
  }, [active, messages]);

  const myOperatorId = me?.operator?.is_active ? me.operator.id : null;
  myOperatorIdRef.current = myOperatorId;
  const canClaim = Boolean(active && myOperatorId && !active.assigned_operator_id && active.status === 'open');

  if (loadingMe) {
    return (
      <MainLayout>
        <div className="flex justify-center py-24"><Spin size="large" /></div>
      </MainLayout>
    );
  }

  if (forbidden || !me) {
    return (
      <MainLayout>
        <Result
          status="403"
          title={<span className="custom-font">دسترسی به صندوق گفتگو ندارید</span>}
          subTitle={<span className="custom-font">برای پاسخ‌گویی، مدیر باید شما را به‌عنوان اپراتور گفتگوی آنلاین تعریف کند.</span>}
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="custom-font" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0 work-page-card" styles={{ body: { padding: 0 } }}>
          <WorkPageHeader
            title="صندوق گفتگوی آنلاین"
            subtitle={me.is_supervisor ? 'نمای سرپرست: همه منابع' : `منابع شما: ${(me.sources || []).map((s) => s.name).join('، ') || '—'}`}
            icon={CommentOutlined}
            headerExtra={(
              <>
                {me.operator && (
                  <Select
                    value={me.operator.status}
                    onChange={changeMyStatus}
                    className="custom-font"
                    style={{ width: 130 }}
                    options={Object.entries(OPERATOR_STATUS).map(([value, meta]) => ({
                      value,
                      label: <Badge color={meta.color === 'default' ? '#9ca3af' : meta.color} text={meta.label} />,
                    }))}
                  />
                )}
                <Tooltip title={muted ? 'صدای پیام جدید خاموش است' : 'صدای پیام جدید روشن است'}>
                  <Button icon={muted ? <MutedOutlined /> : <SoundOutlined />} onClick={toggleMuted} aria-label="صدای پیام جدید" />
                </Tooltip>
                <Button icon={<ReloadOutlined />} onClick={() => loadList()} loading={loadingList} style={{ fontFamily: 'CustomFont' }}>
                  بروزرسانی
                </Button>
              </>
            )}
          />
          <div className="p-4 work-page-body rounded-b-xl">
        <div
          ref={layoutRef}
          className="grid gap-4"
          style={{
            height: paneHeight,
            gridTemplateColumns: isDesktop ? 'minmax(260px, 320px) minmax(0, 1fr)' : 'minmax(0, 1fr)',
          }}
        >
          {/* لیست گفتگوها — در موبایل فقط وقتی گفتگویی باز نیست */}
          <div className={`livechat-panel work-scroll-contain flex-col min-h-0 min-w-0 ${!isDesktop && activeId ? 'hidden' : 'flex'}`}>
            <div className="livechat-panel-bar p-3 border-b shrink-0">
              <ConversationFilters
                filters={filters}
                setFilters={setFilters}
                me={me}
                sites={sites}
                counts={counts}
                departmentCounts={departmentCounts}
                myOperatorId={myOperatorId}
                total={conversations.length}
              />
            </div>
            <div className="work-scroll-pane flex-1 min-h-0 overscroll-contain">
              <Spin spinning={loadingList}>
                {conversations.length === 0 ? (
                  <Empty className="py-12" description={<span className="custom-font">گفتگویی نیست</span>} />
                ) : (
                  conversations.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => openConversation(c.id)}
                      className={`w-full text-start px-3 py-3 border-b border-gray-50 dark:border-white/5 flex gap-3 items-start transition-colors ${
                        c.id === activeId ? 'bg-primary-50 dark:bg-[rgba(13,136,126,0.14)] border-s-2 border-s-brand-primary' : 'hover:bg-gray-50 dark:hover:bg-white/5'
                      }`}
                    >
                      <Tooltip title={c.source?.name} placement="left">
                        <Avatar src={c.contact?.avatar_url} style={{ backgroundColor: c.source?.color }}>
                          {contactName(c.contact).charAt(0)}
                        </Avatar>
                      </Tooltip>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`truncate text-sm ${c.unread_count ? 'font-bold' : 'font-medium'} text-gray-800 dark:text-gray-100`}>
                            {contactName(c.contact)}
                          </span>
                          <span className={`text-[11px] shrink-0 ${c.unread_count ? 'text-brand-primary font-bold' : 'text-gray-400'}`}>{formatChatTime(c.last_message_at)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <span className={`truncate text-xs ${c.unread_count ? 'text-gray-700 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`}>{c.last_message_preview}</span>
                          {c.unread_count > 0 && <Badge count={c.unread_count} size="small" color="#0d887e" />}
                        </div>
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                          <SourceTag source={c.source} compact />
                          <SiteTag site={c.site_host} compact />
                          {c.department && (
                            <Tag color="geekblue" className="custom-font !me-0">
                              {c.department.name}{c.sub_department ? ` › ${c.sub_department.name}` : ''}
                            </Tag>
                          )}
                          {c.flow_state === 'awaiting_department' && <Tag color="gold" className="custom-font !me-0">انتخاب بخش…</Tag>}
                          {c.is_test && <Tag color="purple" className="custom-font !me-0">تست</Tag>}
                          {c.status === 'closed' && <Tag className="custom-font !me-0">بسته</Tag>}
                          {c.assigned_operator ? (
                            <span className="text-[11px] text-gray-400 ms-auto">↤ {operatorName(c.assigned_operator)}</span>
                          ) : (
                            <Tag color="orange" className="custom-font !me-0 ms-auto">بدون اپراتور</Tag>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </Spin>
            </div>
          </div>

          {/* گفتگوی انتخاب‌شده */}
          <div className={`livechat-panel work-scroll-contain flex-col min-h-0 min-w-0 ${!isDesktop && !activeId ? 'hidden' : 'flex'}`}>
            {!activeId ? (
              <div className="flex-1 flex items-center justify-center">
                <Empty description={<span className="custom-font">یک گفتگو را از لیست انتخاب کنید</span>} />
              </div>
            ) : (
              <div className="work-scroll-contain relative flex-1 flex flex-col min-h-0">
                {loadingThread && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-gray-900/50">
                    <Spin />
                  </div>
                )}
                {active && (
                  <div className="livechat-panel-bar px-3 sm:px-4 py-3 border-b flex items-center justify-between gap-2 sm:gap-3 flex-wrap shrink-0 max-h-[45%] work-scroll-pane">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      {!isDesktop && (
                        <Button
                          type="text"
                          icon={<ArrowRightOutlined />}
                          onClick={() => {
                            setActiveId(null);
                            setActive(null);
                          }}
                          aria-label="بازگشت به لیست"
                        />
                      )}
                      <Avatar size={42} src={active.contact?.avatar_url} style={{ backgroundColor: active.source?.color }}>
                        {contactName(active.contact).charAt(0)}
                      </Avatar>
                      <div className="min-w-0">
                        <div className="font-bold text-gray-800 dark:text-gray-100 truncate">{contactName(active.contact)}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                          <SourceTag source={active.source} />
                          {active.contact?.username && <span dir="ltr">@{active.contact.username}</span>}
                          {active.contact?.phone && (
                            <Typography.Text copyable={{ text: active.contact.phone }} className="text-xs" style={{ direction: 'ltr' }}>
                              {active.contact.phone}
                            </Typography.Text>
                          )}
                          {active.channel?.name && <span>· {active.channel.name}</span>}
                        </div>
                        {active.site_host && (
                          <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap mt-1">
                            <SiteTag site={active.site_host} />
                            {active.meta?.page_url && (
                              <Tooltip title={active.meta.page_url}>
                                <a
                                  href={/^https?:\/\//i.test(active.meta.page_url) ? active.meta.page_url : undefined}
                                  target="_blank"
                                  rel="noopener noreferrer nofollow"
                                  className="text-brand-primary hover:underline truncate max-w-[260px] inline-block align-bottom"
                                >
                                  صفحه: {active.meta.page_title || active.meta.page_url}
                                </a>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <Space wrap>
                      {me.is_supervisor && (me.departments || []).length > 0 ? (
                        <Select
                          allowClear
                          placeholder="بخش"
                          value={active.department_id ?? undefined}
                          onChange={(v) => runAction('department', () => liveChatApi.setConversationDepartment(active.id, v ?? null), 'بخش گفتگو تغییر کرد')}
                          loading={actionLoading === 'department'}
                          className="custom-font"
                          style={{ width: 140 }}
                          options={me.departments.map((d) => ({ value: d.id, label: d.name }))}
                        />
                      ) : active.department ? (
                        <Tag color="geekblue" className="custom-font">{active.department.name}</Tag>
                      ) : null}
                      {subDepartmentOptions.length > 0 && (canReply ? (
                        <Select
                          allowClear
                          placeholder="زیربخش"
                          value={active.sub_department_id ?? undefined}
                          onChange={(v) => runAction('sub_department', () => liveChatApi.setConversationSubDepartment(active.id, v ?? null), 'زیربخش گفتگو تغییر کرد')}
                          loading={actionLoading === 'sub_department'}
                          className="custom-font"
                          style={{ width: 150 }}
                          options={subDepartmentOptions}
                        />
                      ) : active.sub_department ? (
                        <Tag color="cyan" className="custom-font">{active.sub_department.name}</Tag>
                      ) : null)}
                      {active.assigned_operator ? (
                        <Tag color="blue" className="custom-font">ارجاع به: {operatorName(active.assigned_operator)}</Tag>
                      ) : null}
                      {canClaim && (
                        <Button
                          icon={<UserAddOutlined />}
                          loading={actionLoading === 'claim'}
                          onClick={() => runAction('claim', () => liveChatApi.assign(active.id, myOperatorId), 'گفتگو به شما ارجاع شد')}
                        >
                          برداشتن گفتگو
                        </Button>
                      )}
                      <Tooltip title="تاریخچه گفتگو">
                        <Button icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)} aria-label="تاریخچه گفتگو" />
                      </Tooltip>
                      {canTransfer && (
                        <Button icon={<SwapOutlined />} onClick={() => setTransferOpen(true)}>
                          ارجاع به همکار
                        </Button>
                      )}
                      {canReply && active.status === 'open' && (
                        <Popconfirm title="این گفتگو بسته شود؟" okText="بله" cancelText="خیر" onConfirm={() => runAction('close', () => liveChatApi.close(active.id), 'گفتگو بسته شد')}>
                          <Button icon={<CheckCircleOutlined />} loading={actionLoading === 'close'}>بستن</Button>
                        </Popconfirm>
                      )}
                      {canReply && active.status === 'closed' && (
                        <Button icon={<UndoOutlined />} loading={actionLoading === 'reopen'} onClick={() => runAction('reopen', () => liveChatApi.reopen(active.id), 'گفتگو باز شد')}>
                          بازگشایی
                        </Button>
                      )}
                    </Space>
                  </div>
                )}

                <div className="relative flex-1 min-h-0 flex flex-col work-scroll-contain">
                  <div className="work-scroll-pane flex-1 min-h-0 overscroll-contain livechat-thread-bg">
                    <ChatMessageList
                      key={activeId}
                      messages={messages}
                      perspective="operator"
                      hasMore={hasMore}
                      onLoadMore={loadOlder}
                      loadingMore={loadingMore}
                      externalLabel={active?.source?.key === 'instagram' ? 'از اپ اینستاگرام' : 'خارج از CRM'}
                    />
                  </div>
                  {historyLoadingIds.has(activeId) && (
                    <div
                      className="livechat-history-overlay absolute inset-0 z-10 flex flex-col items-center justify-center gap-3"
                      role="status"
                      aria-live="polite"
                    >
                      <Spin size="large" />
                      <Typography.Text className="custom-font">در حال دریافت پیام‌های قبلی از اینستاگرام…</Typography.Text>
                    </div>
                  )}
                </div>

                <div className="livechat-panel-bar p-2 sm:p-3 border-t shrink-0">
                  {canReply ? (
                    <>
                    {instagramWindowClosed && (
                      <Alert
                        type="warning"
                        showIcon
                        className="custom-font mb-2"
                        title="بیش از ۲۴ ساعت از آخرین پیام مشتری گذشته است؛ اینستاگرام ممکن است پاسخ را نپذیرد تا مشتری دوباره پیام بدهد."
                      />
                    )}
                    {pendingFiles.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {pendingFiles.map((file, idx) => (
                          <Tag
                            key={`${file.name}-${idx}`}
                            closable
                            icon={file.type.startsWith('image/') ? <PictureOutlined /> : <PaperClipOutlined />}
                            onClose={(e) => {
                              e.preventDefault();
                              setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
                            }}
                            className="custom-font"
                            style={{ direction: 'ltr', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {file.name}
                          </Tag>
                        ))}
                      </div>
                    )}
                    <div
                      className="flex gap-2 items-end"
                      onDragOver={(e) => {
                        if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
                      }}
                      onDrop={(e) => {
                        if (!e.dataTransfer?.files?.length) return;
                        e.preventDefault();
                        addFiles(e.dataTransfer.files);
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        hidden
                        accept={attachmentRules?.accept}
                        onChange={(e) => {
                          addFiles(e.target.files);
                          e.target.value = '';
                        }}
                      />
                      <Tooltip title={`پیوست عکس یا فایل (حداکثر ${attachmentRules?.max_mb ?? 10} مگابایت)`}>
                        <Button icon={<PaperClipOutlined />} onClick={() => fileInputRef.current?.click()} disabled={sending || voice.recording} />
                      </Tooltip>
                      {voice.recording ? (
                        <div className="flex-1 h-9 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 flex items-center gap-2 px-3 text-sm custom-font">
                          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                          <span dir="ltr" className="font-bold tabular-nums">
                            {Math.floor(voice.seconds / 60)}:{String(Math.floor(voice.seconds % 60)).padStart(2, '0')}
                          </span>
                          <span>در حال ضبط…</span>
                          <Tooltip title="لغو ضبط">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => voice.stop(false)} className="!ms-auto" />
                          </Tooltip>
                        </div>
                      ) : (
                      <Input.TextArea
                        onPaste={(e) => {
                          if (e.clipboardData?.files?.length) {
                            e.preventDefault();
                            addFiles(e.clipboardData.files);
                          }
                        }}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onPressEnter={(e) => {
                          if (!e.shiftKey) {
                            e.preventDefault();
                            sendReply();
                          }
                        }}
                        autoSize={{ minRows: 1, maxRows: 5 }}
                        maxLength={5000}
                        placeholder={active?.assigned_operator_id ? 'پاسخ خود را بنویسید… (Enter ارسال، Shift+Enter خط جدید)' : 'با ارسال پاسخ، این گفتگو به شما ارجاع می‌شود'}
                        className="custom-font"
                      />
                      )}
                      {canRecordVoice() && !voice.recording && !draft.trim() && pendingFiles.length === 0 && (
                        <Tooltip title="ضبط پیام صوتی">
                          <Button icon={<AudioOutlined />} onClick={voice.start} disabled={sending} aria-label="ضبط پیام صوتی" />
                        </Tooltip>
                      )}
                      <Tooltip title={voice.recording ? 'ارسال پیام صوتی' : 'ارسال'}>
                        <Button
                          type="primary"
                          icon={<SendOutlined />}
                          onClick={voice.recording ? () => voice.stop(true) : sendReply}
                          loading={sending}
                          disabled={!voice.recording && !draft.trim() && pendingFiles.length === 0}
                          className={PANEL_PRIMARY_BTN_CLASS}
                        />
                      </Tooltip>
                    </div>
                    </>
                  ) : (
                    <div className="text-center text-sm text-gray-500 custom-font">این گفتگو به اپراتور دیگری ارجاع شده و فقط قابل مشاهده است.</div>
                  )}
                </div>
              </div>
            )}
          </div>
          </div>
          </div>
        </Card>
        <ConversationHistoryDrawer open={historyOpen} conversationId={activeId} onClose={() => setHistoryOpen(false)} />
        <TransferConversationModal
          open={transferOpen}
          conversationId={activeId}
          subDepartmentOptions={subDepartmentOptions}
          currentSubDepartmentId={active?.sub_department_id ?? null}
          onClose={() => setTransferOpen(false)}
          onTransferred={(data) => {
            const msg = data?.message;
            if (msg) setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
            refreshActiveMeta();
            scheduleListRefresh();
          }}
        />
      </div>
    </MainLayout>
  );
};

export default LiveChatInbox;
